import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { useJourneyStore } from '@/stores/journeyStore'
import { usePresentationStore } from '@/stores/presentationStore'
import { Home, User, RotateCcw, Settings, Presentation, Users } from 'lucide-react'

export function Header() {
  const navigate = useNavigate()
  const { user, resetJourney } = useJourneyStore()
  const { isPresenterMode, isParticipantMode, currentSession } = usePresentationStore()

  const handleReset = () => {
    if (confirm('Are you sure you want to reset your journey? All progress will be lost.')) {
      resetJourney()
    }
  }

  const handleLogoClick = () => {
    navigate('/landing')  // Always go to professional landing page
  }

  return (
    <header className="border-b bg-white/80 backdrop-blur-md dark:bg-gray-900/80 sticky top-0 z-50">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={handleLogoClick}>
            <div className="p-2 rounded-lg bg-white shadow-sm">
              <img 
                src="/ekow-logo.png" 
                alt="Ekow Logo" 
                className="w-16 h-16 object-contain"
              />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">AI Journey</h1>
              <p className="text-sm text-muted-foreground">From Curious to Capable</p>
            </div>
          </div>

          <nav className="hidden md:flex items-center gap-4">
            <div className="flex items-center gap-2">
              {/* Presentation Mode Indicators */}
              {isPresenterMode && currentSession && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400">
                  <Presentation className="w-4 h-4" />
                  <span className="text-sm font-medium">Presenter</span>
                </div>
              )}
              
              {isParticipantMode && currentSession && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400">
                  <Users className="w-4 h-4" />
                  <span className="text-sm font-medium">Participant</span>
                </div>
              )}

              {/* Regular Controls */}
              {!isPresenterMode && !isParticipantMode && (
                <>
                  {user && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate('/dashboard')}
                      className="gap-2"
                    >
                      <Home className="w-4 h-4" />
                      Dashboard
                    </Button>
                  )}
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/create-session')}
                    className="gap-2"
                  >
                    <Presentation className="w-4 h-4" />
                    Present
                  </Button>
                  
                  {user && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleReset}
                      className="gap-2"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Reset Journey
                    </Button>
                  )}
                </>
              )}
              
              <Button variant="ghost" size="sm" className="gap-2">
                <Settings className="w-4 h-4" />
                Settings
              </Button>
              
              {user && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-secondary">
                  <User className="w-4 h-4" />
                  <span className="text-sm font-medium">{user.name}</span>
                </div>
              )}
            </div>
          </nav>

          {/* Mobile menu button */}
          <Button variant="ghost" size="sm" className="md:hidden">
            <Settings className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </header>
  )
}